/**
 * Portrait emotion vocabulary and alias resolution (pure, unit tested).
 *
 * Canonical portrait frames (integrator decision; the asset pipeline exports whichever of these exist
 * per character): neutral, happy, smile, worried, surprised, frown, sad, angry, tired, hush, excited,
 * dim, cry, laugh, mock, envious, exhausted, chant.
 *
 * Dialogue data may use any canonical name or a free-form intent word (grin, calm, sigh, shout, sell…).
 * `emotionChain()` turns a raw emotion into an ordered list of frame names to try — the name itself,
 * then its alias, then the alias of that… — always ending in `neutral`. The DialogueBox shows the first
 * frame the loaded atlas actually has, so a missing frame never warns: the portrait simply falls back
 * one step at a time (grin → happy → smile → neutral).
 */
export const EMOTIONS = [
  'neutral', 'happy', 'smile', 'worried', 'surprised', 'frown', 'sad', 'angry', 'tired', 'hush',
  'excited', 'dim', 'cry', 'laugh', 'mock', 'envious', 'exhausted', 'chant', 'grin',
] as const;
export type Emotion = (typeof EMOTIONS)[number];
export const NEUTRAL: Emotion = 'neutral';

/** raw / canonical name → next-best canonical name when that frame is missing. */
export const EMOTION_ALIASES: Readonly<Record<string, Emotion>> = {
  // canonical fallbacks (one step towards a frame most portraits ship)
  smile: 'happy', happy: 'smile', laugh: 'happy', excited: 'happy',
  worried: 'frown', frown: 'worried', sad: 'worried', cry: 'sad', envious: 'frown',
  angry: 'frown', mock: 'angry',
  tired: 'dim', dim: 'tired', exhausted: 'tired',
  surprised: 'worried', hush: 'neutral', chant: 'neutral',
  // dialogue-data intents (src/content/**, src/cinematics/**)
  grin: 'happy', bright: 'happy', giggle: 'laugh', dreamy: 'smile', sell: 'grin', proud: 'happy',
  calm: 'neutral', thought: 'neutral', think: 'neutral', serious: 'neutral', respect: 'neutral',
  letter: 'neutral', earnest: 'neutral', ramble: 'neutral', plain: 'neutral',
  sigh: 'tired', weary: 'tired', pain: 'exhausted',
  shout: 'angry', scorn: 'mock', scold: 'angry', smug: 'mock', stern: 'frown', grave: 'frown',
  // art-stage frame names (integrator seam): map to the frames the atlases actually carry
  tearful: 'cry', furrowed: 'frown', resolute: 'neutral', mocking: 'mock',
  whisper: 'hush', urgent: 'worried', sweat: 'worried', nervous: 'worried', afraid: 'worried',
  puzzled: 'surprised', curious: 'surprised', shocked: 'surprised',
  shy: 'dim', flushed: 'dim', prophecy: 'chant',
};

export function normalizeEmotion(raw: string | undefined | null): string {
  return (raw ?? '').trim().toLowerCase();
}

/** Ordered candidate frame names for a raw emotion, always ending with `neutral` (cycle-safe, ≤ 8 steps). */
export function emotionChain(raw: string | undefined | null): string[] {
  const out: string[] = [];
  let cur: string = normalizeEmotion(raw) || NEUTRAL;
  while (cur && !out.includes(cur) && out.length < 8) {
    out.push(cur);
    cur = EMOTION_ALIASES[cur] ?? NEUTRAL;
  }
  if (!out.includes(NEUTRAL)) out.push(NEUTRAL);
  return out;
}

/** Frame in `frames` for an emotion name: exact, or the `<name>-0` form used by the dev atlases. */
export function frameNameFor(name: string, frames: readonly string[]): string | null {
  if (frames.includes(name)) return name;
  const numbered = `${name}-0`;
  return frames.includes(numbered) ? numbered : null;
}

/** First frame of the emotion chain the atlas has, or null when even `neutral` is missing. */
export function resolveEmotionFrame(raw: string | undefined | null, frames: readonly string[]): string | null {
  for (const c of emotionChain(raw)) {
    const f = frameNameFor(c, frames);
    if (f) return f;
  }
  return null;
}
